import { createAsyncThunk } from '@reduxjs/toolkit';
import TransactionsLedgerService from 'services/transactionsLedger/transactionsLedger.service';
import { setFilters, setPage, setSorting } from './slice';

const getRecordsAndMetadata = (response) => {
	const responseData = response?.data || {};
	const records = responseData?.data || [];
	const metadata = responseData?.metadata || {};
	const success = responseData?.success;

	return { records, metadata, success };
};

const normalizeDynamicSearchItems = (items) => {
	if (!Array.isArray(items)) return [];

	return items
		.map((item) => {
			const id = item?.id;
			const name = item?.name;

			if (!name) return null;

			return { id, name };
		})
		.filter(Boolean);
};

const normalizeDealershipItems = (items) => {
	if (!Array.isArray(items)) return [];

	const dealershipMap = new Map();

	items.forEach((item) => {
		const id = item?.id ?? item?.value;
		if (!id) return;

		if (dealershipMap.has(String(id))) return;

		dealershipMap.set(String(id), {
			id,
			name: item?.dealership_name ?? item?.name ?? item?.label ?? item?.text ?? String(id),
		});
	});

	return Array.from(dealershipMap.values());
};

const normalizePaymentMethodItems = (items) => {
	if (!Array.isArray(items)) return [];

	return items
		.map((item) => {
			const value = item?.value;
			const text = item?.label;

			if (!text) return null;

			return {
				value: String(value),
				text,
			};
		})
		.filter(Boolean);
};

export const fetchTransactionsLedgerRecords = createAsyncThunk(
	'transactionsLedger/fetchTransactionsLedgerRecords',
	async (_, { getState, rejectWithValue }) => {
		const {
			transactionsLedger: { query },
		} = getState();

		try {
			const response = await TransactionsLedgerService.instance.getRecords(query);
			const { records, metadata, success } = getRecordsAndMetadata(response);

			if (success === false) {
				return rejectWithValue('Failed to load transactions ledger records.');
			}

			return { records, metadata };
		} catch (error) {
			return rejectWithValue(error?.error?.message || error?.message || 'Failed to load transactions ledger.');
		}
	},
);

export const fetchDealershipOptions = createAsyncThunk(
	'transactionsLedger/fetchDealershipOptions',
	async (name = '', { rejectWithValue }) => {
		try {
			const response = await TransactionsLedgerService.instance.getDealershipOptions(name);
			const options = normalizeDealershipItems(response?.data?.data || response?.data);
			return options;
		} catch (error) {
			return rejectWithValue(error?.error?.message || error?.message || 'Failed to load dealerships.');
		}
	},
);

export const fetchUserOptions = createAsyncThunk(
	'transactionsLedger/fetchUserOptions',
	async ({ name = '' } = {}, { rejectWithValue }) => {
		try {
			const response = await TransactionsLedgerService.instance.getUserOptions(name);
			const options = normalizeDynamicSearchItems(response?.data?.data || response?.data);
			return options;
		} catch (error) {
			return rejectWithValue(error?.error?.message || error?.message || 'Failed to load users.');
		}
	},
);

export const fetchPaymentMethodOptions = createAsyncThunk(
	'transactionsLedger/fetchPaymentMethodOptions',
	async (_, { rejectWithValue }) => {
		try {
			const response = await TransactionsLedgerService.instance.getPaymentMethodOptions();
			const options = normalizePaymentMethodItems(response?.data?.data || response?.data);
			return options;
		} catch (error) {
			return rejectWithValue(error?.error?.message || error?.message || 'Failed to load payment methods.');
		}
	},
);

export const exportTransactionsLedgerCsv = createAsyncThunk(
	'transactionsLedger/exportTransactionsLedgerCsv',
	async (_, { getState, rejectWithValue }) => {
		const {
			transactionsLedger: { query },
		} = getState();

		try {
			await TransactionsLedgerService.instance.exportRecordsCsv(query);
			return true;
		} catch (error) {
			return rejectWithValue(error?.message || 'Failed to export transactions ledger CSV.');
		}
	},
);

export const updateFiltersAndFetch = (newFilters) => async (dispatch) => {
	dispatch(setFilters(newFilters));
	return dispatch(fetchTransactionsLedgerRecords());
};

export const updateSortingAndFetch = (newSorting) => async (dispatch) => {
	dispatch(setSorting(newSorting));
	return dispatch(fetchTransactionsLedgerRecords());
};

export const updatePageAndFetch = (page) => async (dispatch) => {
	dispatch(setPage(page));
	return dispatch(fetchTransactionsLedgerRecords());
};
